import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext } from "react";
import { ensureTrailingSlash, pathLocaleHelper } from "@commercekit/shared";
import { enUS as en, nl, fr } from "date-fns/locale";

export type Locale = string;
interface RouteState {
  locale: Locale;
  routeMap: Record<
    string,
    string | ((path: string, locale: string, ...args: string[]) => string)
  >;
}
const init: RouteState = {
  locale: "",
  routeMap: {},
};
export const RouteCtx = createContext<RouteState>(init);
export const RouteCtxProvider = ({
  children,
  locale,
  routeMap = {},
}: PropsWithChildren<{
  locale: Locale;
  routeMap?: Record<
    string,
    string | ((path: string, locale: string, ...args: string[]) => string)
  >;
}>) => (
  <RouteCtx.Provider value={{ locale, routeMap }}>{children}</RouteCtx.Provider>
);
const DayPickerLocaleMap: Record<string, any> = {
  nl,
  en,
  fr,
};
export const useLocale = () => {
  const { locale: localeFullName, routeMap } = useContext(RouteCtx); //nl-nl
  const locale = localeFullName.split("-")[1] || "";
  return {
    routeMap,
    locale,
    localeFullName: localeFullName,
    localeForDateFns: DayPickerLocaleMap[locale] || DayPickerLocaleMap["nl"],
  };
};

export const useLocalePath = () => {
  const { routeMap } = useLocale();
  return useCallback(
    (path: string, ...args: string[]) => {
      const route = routeMap[path];
      if (route) {
        switch (typeof route) {
          case "string":
            return ensureTrailingSlash(route);
          case "function":
            return ensureTrailingSlash(route(path, "", ...args));
        }
      }
      if (path === "/") return `/`;
      return ensureTrailingSlash(pathLocaleHelper(path, "", ...args));
    },
    [routeMap]
  );
};
